import React, { useState } from 'react';
import { api } from '../../api/client';
import { useAuth } from '../../auth/AuthContext';
import NameSelect from '../../components/NameSelect';
import { ORG_LABELS } from '../../config';
import { useFetch } from '../../hooks';
import { Alert, Button, Field, Input, Select } from '../../ui/basics';
import { Drawer, useToast } from '../../ui/overlay';
import { dayFromNow, rules, useFormValidation } from '../../validation';

const INPUT_TYPES = { email: 'email', phone: 'tel', date: 'date' };

/** One rule list per field, from its `type`: email, phone, url and date are checked when filled; every field has a length limit. */
const schemaFor = (fields) => Object.fromEntries(fields.map((f) => [f.key, [
  ...(f.required ? [rules.required(f.label)] : []),
  ...(f.type === 'email' ? [rules.email(f.label)] : []),
  ...(f.type === 'phone' ? [rules.phone(f.label)] : []),
  ...(f.type === 'url' ? [rules.url(f.label)] : []),
  ...(f.type === 'date' ? [rules.date(f.label, { max: dayFromNow(366) })] : []),
  rules.maxLength(f.max || 50, f.label),
]]));

/** Create / edit drawer shared by clients and recruiters.
 *  sections: [{ title, fields: [{ key, label, required, type, max, options, suggest, full, default, hint }] }]
 *  type 'person' is a dropdown of the organization's recruiters, so a name can only be picked, never typed.
 *  suggestions: { <facet name>: [values] } offered as type-ahead choices for fields with `suggest`. */
export default function RecordDrawer({ kind, noun, record, org, sections, idKey, suggestions = {}, onClose, onSaved }) {
  const { orgs } = useAuth();
  const toast = useToast();
  const editing = Boolean(record);
  const writable = orgs.filter((o) => o !== 'combined');
  const fields = sections.flatMap((s) => s.fields);
  const [orgKey, setOrgKey] = useState(editing ? record.organization : (writable.includes(org) ? org : writable[0]));
  const [form, setForm] = useState(() => Object.fromEntries(fields.map((f) => [f.key, record ? (record[f.key] ?? '') : (f.default ?? '')])));
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const original = record ? Object.fromEntries(fields.map((f) => [f.key, record[f.key] ?? ''])) : null;
  const v = useFormValidation(form, schemaFor(fields), original);
  const lookups = useFetch(fields.some((f) => f.type === 'person') ? 'lookups/' : null, { organization: orgKey });

  const save = async () => {
    setError('');
    if (!v.check()) return;
    setBusy(true);
    try {
      if (editing) {
        // Only send what changed: old rows may hold values the server would no longer accept as new input.
        const changed = Object.fromEntries(fields.filter((f) => String(form[f.key] ?? '') !== String(record[f.key] ?? '')).map((f) => [f.key, form[f.key]]));
        if (Object.keys(changed).length) await api.patch(`${kind}/${record[idKey]}/`, changed);
        toast(Object.keys(changed).length ? `${noun} updated` : 'No changes to save', Object.keys(changed).length ? 'success' : '');
      } else {
        await api.post(`${kind}/`, { ...form, organization: orgKey });
        toast(`${noun} added`, 'success');
      }
      onSaved();
    } catch (e) {
      setError(v.fail(e) ? 'The server found a problem with the highlighted fields.' : e.message);
      setBusy(false);
    }
  };

  const control = (f) => {
    const common = { value: form[f.key] ?? '', onChange: (e) => set(f.key, e.target.value), ...v.props(f.key) };
    if (f.type === 'select') return <Select options={f.options} {...common} />;
    if (f.type === 'person') {
      const self = record && f.key === 'reporting_to' ? [record.display_name] : [];
      return <NameSelect kind="recruiter" lookups={lookups} current={original ? original[f.key] : ''} exclude={self} placeholder="None" {...common} />;
    }
    const list = f.suggest && suggestions[f.suggest] ? `${kind}-${f.key}-list` : undefined;
    return (
      <>
        <Input type={INPUT_TYPES[f.type] || 'text'} list={list} autoComplete="off" maxLength={(f.max || 50) + 20} {...common} />
        {list && <datalist id={list}>{suggestions[f.suggest].map((v) => <option key={v} value={v} />)}</datalist>}
      </>
    );
  };

  return (
    <Drawer title={editing ? `Edit ${noun.toLowerCase()}` : `Add ${noun.toLowerCase()}`} onClose={onClose}
      footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" loading={busy} onClick={save}>{editing ? 'Save changes' : `Add ${noun.toLowerCase()}`}</Button></>}>
      <div className="stack" style={{ gap: 24 }}>
        {(v.summary || error) && <Alert>{error || v.summary}</Alert>}
        {editing && record.placements > 0 && (
          <Alert tone="info">{record.placements} placement{record.placements === 1 ? '' : 's'} use this {noun.toLowerCase()}&apos;s name. Renaming does not change those placements; they keep the old name.</Alert>
        )}
        <Field label="Organization" hint={editing ? 'The organization of an existing record cannot be changed.' : undefined}>
          {editing ? <Input value={ORG_LABELS[orgKey]} readOnly /> : <Select value={orgKey} onChange={(e) => setOrgKey(e.target.value)} options={writable.map((o) => ({ value: o, label: ORG_LABELS[o] }))} />}
        </Field>
        {sections.map((section) => (
          <div key={section.title} className="stack" style={{ gap: 12 }}>
            <h3>{section.title}</h3>
            <div className="form-grid">
              {section.fields.map((f) => (
                <Field key={f.key} label={f.required ? `${f.label} *` : f.label} className={f.full ? 'full' : ''}
                  hint={f.hint || (f.type === 'person' ? "Pick from this organization's recruiters." : undefined)} error={v.shown(f.key)}>{control(f)}</Field>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Drawer>
  );
}
